
'use client';

import React from 'react';
import { DemoBanner } from '@/components/DemoBanner';
import { GraduationCap, BookOpen, Users, Award, Star, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function EducationDemo() {
  const courses = [
    { title: "Web Dasturlash", category: "Dasturlash", duration: "3 oy", level: "Boshlang'ich", price: "1 200 000 so'm", color: "bg-blue-500" },
    { title: "Grafik Dizayn", category: "Dizayn", duration: "2 oy", level: "O'rta", price: "900 000 so'm", color: "bg-purple-500" },
    { title: "SMM va Marketing", category: "Marketing", duration: "1.5 oy", level: "Boshlang'ich", price: "700 000 so'm", color: "bg-orange-500" },
    { title: "Python & AI", category: "Dasturlash", duration: "4 oy", level: "O'rta", price: "1 500 000 so'm", color: "bg-green-500" },
    { title: "English Tili", category: "Tillar", duration: "6 oy", level: "Boshlang'ich", price: "800 000 so'm", color: "bg-red-500" },
    { title: "Video Montaj", category: "Media", duration: "2 oy", level: "O'rta", price: "950 000 so'm", color: "bg-pink-500" },
  ];

  return (
    <div className="min-h-screen bg-white pt-11 font-sans selection:bg-blue-600 selection:text-white">
      <DemoBanner />

      {/* NAVIGATION */}
      <nav className="border-b border-gray-100 py-4 sticky top-11 bg-white z-40">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2 text-2xl font-bold text-blue-600">
            <GraduationCap size={32} />
            <span className="tracking-tight text-gray-900">EduPro</span>
          </div>
          <div className="hidden lg:flex items-center gap-8 text-sm font-medium text-gray-600">
            <a href="#" className="text-blue-600">Kurslar</a>
            <a href="#" className="hover:text-blue-600 transition-colors">O'qituvchilar</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Jadval</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Narxlar</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Blog</a>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6">
            Ro'yxatdan o'tish
          </Button>
        </div>
      </nav>

      {/* HERO */}
      <section className="py-20 md:py-32 bg-blue-50/50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 leading-[1.1] mb-8">
                Kelajagingizni <br /> <span className="text-blue-600">Bugun Quring</span>
              </h1>
              <p className="text-xl text-gray-600 mb-12 max-w-lg leading-relaxed">
                Professional kurslar, tajribali o'qituvchilar va kafolatlangan natija bilan o'z karyerangizni yangi bosqichga olib chiqing.
              </p>
              
              <div className="flex flex-wrap gap-12 mb-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">2000+</div>
                  <div className="text-sm text-gray-500 font-medium">O'quvchi</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">50+</div>
                  <div className="text-sm text-gray-500 font-medium">Kurslar</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">95%</div>
                  <div className="text-sm text-gray-500 font-medium">Ish topadi</div>
                </div>
              </div>

              <Button className="bg-blue-600 hover:bg-blue-700 text-white h-14 px-10 text-lg group">
                Kurslarni Ko'rish <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            
            <div className="hidden lg:block relative">
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-30 animate-pulse" />
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-purple-200 rounded-full blur-3xl opacity-30 animate-pulse" />
              <div className="relative bg-white p-8 rounded-[2rem] shadow-2xl shadow-blue-900/10 border border-blue-50 grid grid-cols-2 gap-6">
                <div className="aspect-square bg-blue-50 rounded-2xl flex flex-col items-center justify-center text-blue-600">
                  <BookOpen size={48} />
                  <span className="mt-4 font-bold">Darslar</span>
                </div>
                <div className="aspect-square bg-purple-50 rounded-2xl flex flex-col items-center justify-center text-purple-600">
                  <Users size={48} />
                  <span className="mt-4 font-bold">Jamoa</span>
                </div>
                <div className="aspect-square bg-orange-50 rounded-2xl flex flex-col items-center justify-center text-orange-600">
                  <Award size={48} />
                  <span className="mt-4 font-bold">Sertifikat</span>
                </div>
                <div className="aspect-square bg-green-50 rounded-2xl flex flex-col items-center justify-center text-green-600">
                  <Star size={48} />
                  <span className="mt-4 font-bold">Reyting</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section className="py-32">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Mashhur Kurslar</h2>
            <p className="text-gray-500">O'zingizga mos yo'nalishni tanlang va o'qishni boshlang.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c, i) => (
              <div key={i} className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-300">
                <div className={`h-40 ${c.color} p-8 flex items-end justify-between`}>
                  <div className="bg-white/20 backdrop-blur-md rounded-full px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider">
                    {c.category}
                  </div>
                  <Award className="text-white/20" size={80} />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-4">{c.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-8">
                    <span className="flex items-center gap-1"><BookOpen size={14} /> {c.duration}</span>
                    <span className="flex items-center gap-1"><Star size={14} /> {c.level}</span>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                    <div className="text-lg font-bold text-gray-900">{c.price}</div>
                    <Button variant="ghost" className="text-blue-600 hover:bg-blue-50 font-bold">Ro'yxat →</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-16 mb-20">
            <div className="col-span-2">
              <div className="flex items-center gap-2 text-2xl font-bold text-blue-400 mb-8">
                <GraduationCap size={32} />
                <span className="tracking-tight text-white">EduPro</span>
              </div>
              <p className="text-gray-400 max-w-sm leading-relaxed mb-8">
                Sifatli ta'lim — bu muvaffaqiyatli kelajak asosi. Biz bilan birga eng zamonaviy bilimlarni egallang.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <Phone size={18} className="text-blue-400" /> +998 71 200 00 00
                </div>
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <Mail size={18} className="text-blue-400" /> info@edupro.uz
                </div>
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <MapPin size={18} className="text-blue-400" /> Toshkent sh., Chilonzor tumani
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-8">Kurslar</h4>
              <ul className="space-y-4 text-sm text-gray-400">
                <li><a href="#" className="hover:text-blue-400">Dasturlash</a></li>
                <li><a href="#" className="hover:text-blue-400">Dizayn</a></li>
                <li><a href="#" className="hover:text-blue-400">Marketing</a></li>
                <li><a href="#" className="hover:text-blue-400">Tillar</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-8">Kompaniya</h4>
              <ul className="space-y-4 text-sm text-gray-400">
                <li><a href="#" className="hover:text-blue-400">Haqimizda</a></li>
                <li><a href="#" className="hover:text-blue-400">O'qituvchilar</a></li>
                <li><a href="#" className="hover:text-blue-400">Blog</a></li>
                <li><a href="#" className="hover:text-blue-400">Aloqa</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-xs text-gray-500 flex justify-between">
            <span>© 2026 EduPro Learning Center</span>
            <div className="flex gap-8">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
